import { vestResolver } from '@hookform/resolvers/vest'
import { useIfpFormContext } from '@sec-loans-ui/ifp-loans/steps/formContext'
import { FormStep } from '@sec-loans-ui/ifp-loans/steps/FormStep'
import { assertValue } from '@shared/helpers'
import { FinnishPhoneNumberInput } from '@ui-common/components/FinnishPhoneNumberInput'
import { NameFormSection } from '@ui-common/components/NameFormSection'
import { useWizardForm } from '@ui-common/hooks'
import { ValidationReSubmitFallback } from '@ui-common/state/ValidationReSubmitFallback'
import { PHONE_COUNTRY_CODE } from '@ui-common/types'
import { trimPhoneNumber } from '@ui-common/utils'
import { getProgressBarProps } from '@ui-common/utils/wizardUtils'
import { Divider } from '@ui-components/decorative/Divider'
import { FormSectionContainer } from '@ui-components/form/FormSectionContainer'
import { FormTextInput } from '@ui-components/form/FormTextInput'
import { RegionWithLabel } from '@ui-components/form/RegionWithLabel'
import { LargeHeading } from '@ui-components/typography/headings/headingPrimitives'
import { useLayoutEffect, useMemo } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { getAnalyticsNameForPage } from '../../analytics/service'
import { getProgressInfo } from '../../utils'
import { IfpWizardType } from '../../wizard/wizardMachine'
import { wizardDataToFormData } from './applicantInfoStepUtils'
import { ExpensesFormFields } from './ExpensesFormFields'
import { createFormValidator } from './formValidator'
import { OccupationFormPart } from './OccupationFormPart'
import { OtherIncomeFormPart } from './OtherIncomeFormPart'
import { ApplicantInfoFormData } from './types'

export const ApplicantInfoStep = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const {
    step,
    wizardData,
    proceed,
    currentApplicantInfoIndex,
    wizardType,
    loan: { debtors },
  } = useIfpFormContext()
  const isShortFlow = wizardType === IfpWizardType.Short
  const isLongFlow = !isShortFlow

  const currApplicant = assertValue(debtors[currentApplicantInfoIndex], 'debtors[currentApplicantInfoIndex]')
  const formDefaultValues = wizardData?.applicantInfos?.find((a) => a.uuid === currApplicant.uuid)

  // Scroll to top when this component re-mounts (currentApplicantInfoIndex updates which is used as key for the component)
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const methods = useWizardForm<ApplicantInfoFormData>({
    defaultValues: formDefaultValues ? wizardDataToFormData(formDefaultValues) : undefined,
    resolver: vestResolver(
      useMemo(
        () => createFormValidator({ formatMessage, isShortFlow, isAsyncValidationEnabled: true }),
        [formatMessage, isShortFlow],
      ),
    ),
  })

  const {
    formState: { isSubmitting },
    handleSubmit,
  } = methods

  const onSubmit = (formData: ApplicantInfoFormData) => {
    const updatedApplicantInfos = wizardData?.applicantInfos ? [...wizardData?.applicantInfos] : []
    updatedApplicantInfos[currentApplicantInfoIndex] = {
      ...formData,
      phoneNumber: `${PHONE_COUNTRY_CODE}${trimPhoneNumber(assertValue(formData.phoneNumber, 'formData.phoneNumber'))}`,
      uuid: currApplicant.uuid,
    }
    proceed({ applicantInfos: updatedApplicantInfos })
  }

  const getPageNameForAnalytics = () => {
    if (debtors.length > 1) {
      return `(${currentApplicantInfoIndex + 1}) ${getAnalyticsNameForPage(step)}`
    }
    return getAnalyticsNameForPage(step)
  }

  return (
    <FormStep
      subHeader={`${formatMessage({ id: 'ifp-loans-applicant-info-step-heading' })} (${currentApplicantInfoIndex + 1})`}
      formMethods={methods}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={isSubmitting}
      progressBarOptions={getProgressBarProps({
        intl,
        progressInfo: getProgressInfo({
          step,
          debtorsCount: debtors.length,
          type: wizardType,
          currentApplicantIndex: currentApplicantInfoIndex,
        }),
        showProgressBar: true,
      })}
      pageNameForAnalytics={getPageNameForAnalytics()}
    >
      <ValidationReSubmitFallback onSubmit={onSubmit} />
      <FormSectionContainer>
        <RegionWithLabel ariaLabelledBy="background-info-heading">
          <LargeHeading id="background-info-heading">
            {formatMessage({ id: 'ifp-loans-background-info-heading' })}
          </LargeHeading>
          <NameFormSection
            user={{
              firstNames: currApplicant.name,
              lastName: '',
            }}
            label={formatMessage({ id: 'common-cards-contact-info-name-label' })}
          />
          <FinnishPhoneNumberInput
            label={formatMessage({ id: 'common-phone-number' })}
            description={formatMessage({ id: 'ifp-loans-background-info-phone-number-description' })}
            phoneNumberField="phoneNumber"
          />
        </RegionWithLabel>
        {isLongFlow && (
          <>
            <Divider />
            <RegionWithLabel ariaLabelledBy="income-info-heading">
              <LargeHeading id="income-info-heading">
                {formatMessage({ id: 'ifp-loans-income-info-heading' })}
              </LargeHeading>
              <IncomeFormFields />
            </RegionWithLabel>
            <Divider />
            <RegionWithLabel ariaLabelledBy="expenses-info-heading">
              <LargeHeading id="expenses-info-heading">
                {formatMessage({ id: 'ifp-loans-applicant-info-expenses-heading' })}
              </LargeHeading>
              <ExpensesFormFields />
            </RegionWithLabel>
          </>
        )}
      </FormSectionContainer>
    </FormStep>
  )
}

const IncomeFormFields = () => {
  const { formatMessage } = useIntl()
  const { register, control } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({ name: ['totalSavings'], control })

  return (
    <>
      <OccupationFormPart />
      <OtherIncomeFormPart />
      <FormTextInput
        required={true}
        label={formatMessage({ id: 'ifp-loans-applicant-info-all-savings-label' })}
        description={formatMessage({ id: 'ifp-loans-applicant-info-all-saving-description' })}
        error={errors?.totalSavings?.message}
        {...register('totalSavings')}
      />
    </>
  )
}
